/** Reminders (spec §8.10): tabbed by status, one-click completion, related-entity links. */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Plus, RotateCcw } from 'lucide-react';
import { createReminderSchema, today, type ReminderDto } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, Tooltip } from '@/components/ui/misc';
import { DataTable, type DataColumn } from '@/components/data-table/data-table';
import { AutoForm, type FieldDescriptor } from '@/components/form/auto-form';
import { EnumBadge } from '@/components/common/enum-badge';
import { PageHeader } from '@/components/common/page-header';
import { FilterBar, type FilterDescriptor } from '@/components/filters/filter-bar';
import { remindersApi, useAssignableUsers } from '@/lib/api/domains';
import { useOfflineMutation, PENDING_FLAG } from '@/lib/offline/use-offline-mutation';
import { PendingSyncBadge } from '@/components/offline/offline-indicator';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { entityRoute } from '@/lib/registry/navigation';
import { useListState } from '@/hooks/use-list-state';

const TABS = [
  { value: '', labelKey: 'reminder.tabAll', countKey: 'all' },
  { value: 'pending', labelKey: 'reminder.tabPending', countKey: 'pending' },
  { value: 'overdue', labelKey: 'reminder.tabOverdue', countKey: 'overdue' },
  { value: 'completed', labelKey: 'reminder.tabCompleted', countKey: 'completed' },
] as const;

export function RemindersPage() {
  const { t, date } = useI18n();
  const navigate = useNavigate();
  const { can } = useAuth();

  const list = useListState({ defaultSortBy: 'reminderDate' });
  const { data, isLoading, isFetching, error, refetch } = remindersApi.useList(list.params);
  const { data: agents } = useAssignableUsers();

  // Completing a reminder is safe offline: it is a flag on the agent's own task list,
  // it carries no money and setting it twice means what setting it once means.
  const complete = useOfflineMutation<{ id: string; title: string }>({
    operation: 'reminder.complete',
    targetId: (input) => input.id,
    summary: (input) => input.title,
  });
  const reopen = useOfflineMutation<{ id: string; title: string }>({
    operation: 'reminder.reopen',
    targetId: (input) => input.id,
    summary: (input) => input.title,
  });
  const [formOpen, setFormOpen] = React.useState(false);

  const rows = (data?.data ?? []) as unknown as ReminderDto[];
  const counts = (data?.meta as { counts?: Record<string, number> } | undefined)?.counts;
  const activeTab = list.filters.status ?? '';

  const columns = React.useMemo<DataColumn<ReminderDto>[]>(
    () => [
      {
        id: 'title',
        header: t('reminder.one'),
        meta: { sortKey: 'title', headerLabel: t('reminder.one') },
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <span
              className={
                row.original.status === 'completed' ? 'text-muted-foreground line-through' : 'font-medium'
              }
            >
              {row.original.title}
            </span>
            {PENDING_FLAG in row.original && <PendingSyncBadge />}
          </span>
        ),
      },
      {
        id: 'relatedEntity',
        header: t('reminder.relatedTo'),
        meta: { sortKey: 'relatedEntity', hideBelow: 'md', headerLabel: t('reminder.relatedTo') },
        cell: ({ row }) =>
          row.original.relatedEntityLabel && row.original.relatedEntityType ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                navigate(entityRoute(row.original.relatedEntityType!, row.original.relatedEntityId!));
              }}
              className="text-primary truncate hover:underline"
            >
              {row.original.relatedEntityLabel}
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'reminderDate',
        header: t('reminder.date'),
        meta: { sortKey: 'reminderDate', numeric: true, headerLabel: t('reminder.date') },
        cell: ({ row }) => (
          <span className="flex items-center gap-2 whitespace-nowrap">
            {date(row.original.reminderDate)}
            {row.original.status === 'overdue' && (row.original.daysOverdue ?? 0) > 0 && (
              <Badge tone="critical" size="sm" className="tabular">
                +{row.original.daysOverdue}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'priority',
        header: t('reminder.priority'),
        meta: { sortKey: 'priority', hideBelow: 'lg', headerLabel: t('reminder.priority') },
        cell: ({ row }) => <EnumBadge enum="priority" value={row.original.priority} size="sm" />,
      },
      {
        id: 'status',
        header: t('policy.status'),
        meta: { sortKey: 'status', headerLabel: t('policy.status') },
        cell: ({ row }) => <EnumBadge enum="reminderStatus" value={row.original.status} size="sm" />,
      },
      {
        id: 'assignedTo',
        header: t('reminder.assignedTo'),
        meta: { sortKey: 'assignedTo', hideBelow: 'xl', headerLabel: t('reminder.assignedTo') },
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.assignedToName ?? t('common.unassigned')}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        meta: { width: '3rem', align: 'right' },
        cell: ({ row }) =>
          can('reminder:write') && (
            <div onClick={(event) => event.stopPropagation()}>
              {row.original.status === 'completed' ? (
                <Tooltip content={t('action.reopen')}>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => void reopen.mutate({ id: row.original.id, title: row.original.title })}
                    aria-label={t('action.reopen')}
                  >
                    <RotateCcw />
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip content={t('action.markComplete')}>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => void complete.mutate({ id: row.original.id, title: row.original.title })}
                    aria-label={t('action.markComplete')}
                  >
                    <Check className="text-good" />
                  </Button>
                </Tooltip>
              )}
            </div>
          ),
      },
    ],
    [t, date, navigate, can, complete, reopen],
  );

  /*
   * One declaration, two homes: a filter naming a `column` is rendered in the table's
   * filter row under that column, the rest stay in the toolbar, and the narrow-screen
   * popover keeps all of them.
   */
  const filters: FilterDescriptor[] = [
    { key: 'priority', type: 'enum', enum: 'priority', label: t('reminder.priority'), multiple: true, column: 'priority' },
    {
      key: 'assignedTo',
      column: 'assignedTo',
      type: 'options',
      label: t('reminder.assignedTo'),
      options: (agents ?? []).map((agent) => ({ value: agent.id, label: agent.fullName })),
    },
    { key: 'dateFrom', type: 'date', label: t('common.from'), column: 'reminderDate' },
    { key: 'dateTo', type: 'date', label: t('common.to') },
  ];

  return (
    <>
      <PageHeader
        title={t('reminder.title')}
        description={t('reminder.subtitle')}
        actions={
          can('reminder:write') && (
            <Button onClick={() => setFormOpen(true)}>
              <Plus /> {t('action.newReminder')}
            </Button>
          )
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={(value) => list.setFilter('status', value || undefined)}
        className="mb-5"
      >
        <TabsList className="flex w-full justify-start sm:w-fit">
          {TABS.map((tab) => (
            <TabsTrigger key={tab.value || 'all'} value={tab.value}>
              {t(tab.labelKey)}
              {counts?.[tab.countKey] !== undefined && (
                <span className="text-muted-foreground ml-1 tabular">{counts[tab.countKey]}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <FilterBar
        className="mb-5"
        listState={list}
        inline="unbound"
        searchPlaceholder={t('reminder.one')}
        filters={filters}
      />

      <DataTable
        columns={columns}
        rows={rows}
        total={data?.meta.total}
        listState={list}
        filters={filters}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRetry={refetch}
        getRowId={(reminder) => reminder.id}
        rowClassName={(reminder) => (reminder.status === 'overdue' ? 'row-tint-critical' : undefined)}
      />

      <ReminderFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </>
  );
}

function ReminderFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const { data: agents } = useAssignableUsers();
  const create = useOfflineMutation<Record<string, unknown>>({
    operation: 'reminder.create',
    summary: (input) => String(input.title ?? ''),
    // Show it in the list straight away, flagged, so a queued reminder is visible
    // rather than merely promised.
    optimistic: (input, entryId) => ({
      queryKey: ['reminders'],
      record: {
        id: entryId,
        title: input.title,
        reminderDate: input.reminderDate,
        priority: input.priority ?? 'normal',
        status: 'pending',
        notes: input.notes ?? null,
        clientId: null,
        policyId: null,
        claimId: null,
        relatedEntityType: null,
        relatedEntityId: null,
        relatedEntityLabel: null,
        assignedTo: null,
        assignedToName: null,
        completedAt: null,
        createdBy: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        daysOverdue: 0,
        [PENDING_FLAG]: true,
      },
    }),
    onSuccessMessage: 'common.saved',
  });
  const formId = 'reminder-form';

  const fields: FieldDescriptor<Record<string, unknown>>[] = [
    { name: 'title', type: 'text', label: t('reminder.one'), required: true, span: 2 },
    { name: 'reminderDate', type: 'date', label: t('reminder.date'), required: true },
    { name: 'priority', type: 'enum', enum: 'priority', label: t('reminder.priority') },
    {
      name: 'assignedTo',
      type: 'select',
      label: t('reminder.assignedTo'),
      options: (agents ?? []).map((agent) => ({ value: agent.id, label: agent.fullName })),
    },
    { name: 'notes', type: 'textarea', label: t('common.notes') },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('action.newReminder')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <AutoForm
            id={formId}
            schema={createReminderSchema}
            defaultValues={
              {
                title: '',
                reminderDate: today(),
                priority: 'normal',
                assignedTo: '',
                notes: '',
              } as never
            }
            fields={fields}
            onSubmit={async (values) => {
              const payload = { ...values };
              if (!payload.assignedTo) delete payload.assignedTo;
              await create.mutate(payload);
              onOpenChange(false);
            }}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button type="submit" form={formId} loading={create.isPending}>
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
